import { Alert, Button, Space, Typography } from 'antd'
import { Link } from 'react-router-dom'

import { useUsage } from '../api/hooks'

/**
 * What a customer on a trial is told, and when.
 *
 * Before this existed, the first signal that a trial had ended was a 403 on
 * ingest: the SDK stopped uploading and the service went quiet. That reads as
 * an outage, not as a bill — people would have gone looking for the fault in
 * their own deployment, and the one place it could not be found is the one
 * place it was.
 *
 * So the banner is not dismissible. A notice you can close is a notice that is
 * closed on the day it starts mattering, and the whole point is that the end of
 * the trial should never be a surprise.
 *
 * Nothing is shown once a plan is paid for. Showing a customer who has already
 * bought something a permanent reminder to buy it is the fastest way to teach
 * them to ignore the banner.
 */
export function TrialBanner() {
  const { data } = useUsage()
  const trial = data?.trial

  if (!trial || trial.purchased) {
    return null
  }

  if (trial.expired) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message="The trial has ended"
        description={
          <Space direction="vertical" size={8}>
            <Typography.Text>
              New profiles are not being accepted. Everything already collected stays here and
              stays readable — the flame graphs, the trends and the links all still work.
            </Typography.Text>
            <Link to="/pricing">
              <Button type="primary" size="small">
                Choose a plan
              </Button>
            </Link>
          </Space>
        }
      />
    )
  }

  if (!trial.active) {
    return null
  }

  // The last few days get a louder colour. Saying "9 days left" and "1 day
  // left" in the same tone leaves the reader to notice the number, and the
  // number is the whole message.
  const ending = trial.days_left <= 3

  return (
    <Alert
      type={ending ? 'warning' : 'info'}
      showIcon
      style={{ marginBottom: 16 }}
      message={
        <Space wrap size={12}>
          <Typography.Text strong>
            {trial.days_left === 1 ? '1 day' : `${trial.days_left} days`} left of your{' '}
            {trial.plan_name} trial
          </Typography.Text>
          <Link to="/pricing">
            <Button type={ending ? 'primary' : 'link'} size="small">
              See plans
            </Button>
          </Link>
        </Space>
      }
      description={
        ending ? (
          <Typography.Text type="secondary">
            When it ends, new profiles stop being accepted. What you have already collected stays
            readable either way.
          </Typography.Text>
        ) : undefined
      }
    />
  )
}
